import { isMobileDevice, isIOS } from './deviceDetection';
import { fadeMobileGain, getMobileGainNode, ensureAudioContextResumed } from './mobileAudioContextManager';

const DEBUG = false;

const debugLog = (message: string, data?: Record<string, unknown>) => {
  if (DEBUG) {
    console.log(`[MobileSwitch] ${message}`, data || '');
  }
};

interface MobilePlayerSwitchingConfig {
  audioElements: Map<number, HTMLAudioElement>;
  selectedPlayer: number;
  gainOffsets: Map<number, number>;
  isBlindTestMode: boolean;
  isPlaying: boolean;
}

export const switchPlayerMobile = async (config: MobilePlayerSwitchingConfig): Promise<void> => {
  const { audioElements, selectedPlayer, gainOffsets, isBlindTestMode, isPlaying } = config;
  
  if (!isMobileDevice()) return;
  
  debugLog('Starting player switch', { selectedPlayer });

  try {
    // Ensure audio context is resumed before switching
    await ensureAudioContextResumed();
    
    // Determine the previously audible player by checking gain values
    const previouslySelectedPlayer = Array.from(audioElements.keys()).find(playerNumber => {
      const gainNode = getMobileGainNode(playerNumber);
      return gainNode && gainNode.gain.value > 0;
    });

    // Do not re-prepare or re-connect elements here; they are connected once on wavesurfer ready.
    // Just proceed with gain crossfade and playback assurance.

    if (previouslySelectedPlayer && previouslySelectedPlayer !== selectedPlayer) {
      debugLog(`Fading out previously selected player ${previouslySelectedPlayer}`);
      await fadeMobileGain(previouslySelectedPlayer, 0, 20);
    }

    const selectedAudio = audioElements.get(selectedPlayer);
    const selectedGain = gainOffsets.get(selectedPlayer) || 1;

    if (selectedAudio) {
      debugLog(`Fading in selected player ${selectedPlayer}`, { gainOffset: selectedGain });
      await fadeMobileGain(selectedPlayer, selectedGain, 20);
    }

    // Ensure playback for the selected player when app is playing (or blind test requires it)
    if (selectedAudio) {
      debugLog(`Starting playback for player ${selectedPlayer}`, {
        readyState: selectedAudio.readyState,
        paused: selectedAudio.paused
      });
      
      if (selectedAudio.paused && (isBlindTestMode || isPlaying)) {
        try {
          // Ensure context is resumed before playing
          await ensureAudioContextResumed();
          
          // For iOS, we need to ensure the audio element is properly loaded
          if (isIOS() && selectedAudio.readyState < 2) {
            debugLog('iOS detected, waiting for audio element to load');
            
            // CRITICAL: Preserve currentTime from other players before loading
            let preservedTime = 0;
            if (previouslySelectedPlayer) {
              const previousAudio = audioElements.get(previouslySelectedPlayer);
              if (previousAudio && previousAudio.currentTime > 0) {
                preservedTime = previousAudio.currentTime;
                debugLog(`Preserving currentTime ${preservedTime}s from player ${previouslySelectedPlayer}`);
              }
            }
            
            try {
              await new Promise<void>((resolve, reject) => {
                const loadHandler = () => {
                  selectedAudio.removeEventListener('canplaythrough', loadHandler);
                  // CRITICAL: Restore currentTime after loading completes
                  if (preservedTime > 0) {
                    selectedAudio.currentTime = preservedTime;
                    debugLog(`Restored currentTime to ${preservedTime}s for player ${selectedPlayer}`);
                  }
                  resolve();
                };
                const errorHandler = (e: Event) => {
                  selectedAudio.removeEventListener('error', errorHandler);
                  reject(new Error('Failed to load audio element'));
                };
                selectedAudio.addEventListener('canplaythrough', loadHandler);
                selectedAudio.addEventListener('error', errorHandler);
                
                // For iOS, we need to trigger loading explicitly
                try {
                  selectedAudio.load(); // This resets currentTime to 0, but we restore it in loadHandler
                } catch (error) {
                  console.warn(`Failed to load audio element for player ${selectedPlayer}:`, error);
                  // Don't reject here, let the event handlers handle it
                }
              });
            } catch (error) {
              console.warn(`Failed to load audio element for player ${selectedPlayer}:`, error);
              // For iOS, try one more time
              if (isIOS()) {
                debugLog(`Retrying audio element load for player ${selectedPlayer}`);
                try {
                  await new Promise<void>((resolve, reject) => {
                    const loadHandler = () => {
                      selectedAudio.removeEventListener('canplaythrough', loadHandler);
                      // CRITICAL: Restore currentTime after retry loading completes
                      if (preservedTime > 0) {
                        selectedAudio.currentTime = preservedTime;
                        debugLog(`Restored currentTime to ${preservedTime}s for player ${selectedPlayer} after retry`);
                      }
                      resolve();
                    };
                    const errorHandler = (e: Event) => {
                      selectedAudio.removeEventListener('error', errorHandler);
                      reject(new Error('Failed to load audio element on retry'));
                    };
                    selectedAudio.addEventListener('canplaythrough', loadHandler);
                    selectedAudio.addEventListener('error', errorHandler);
                    selectedAudio.load(); // This resets currentTime to 0, but we restore it in loadHandler
                  });
                } catch (retryError) {
                  console.error(`Failed to load audio element for player ${selectedPlayer} after retry:`, retryError);
                  throw retryError;
                }
              } else {
                throw error;
              }
            }
          }
          
          await selectedAudio.play();
          debugLog(`Playback started for player ${selectedPlayer}`);
        } catch (error) {
          console.warn(`Failed to start playback for player ${selectedPlayer}:`, error);
        }
      }
    }
  } catch (error) {
    console.error('Failed to switch players:', error);
  }
}; 